import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Clock, Shield, Banknote, CheckCircle } from 'lucide-react';

const trustlyBenefits = [
  {
    icon: <Clock className="w-6 h-6" />,
    title: "0-15 Minutter",
    description: "Lynhurtige udbetalinger direkte til din bankkonto"
  },
  {
    icon: <Shield className="w-6 h-6" />,
    title: "Banksikkerhed",
    description: "Samme sikkerhedsniveau som din netbank"
  },
  {
    icon: <Banknote className="w-6 h-6" />,
    title: "Ingen Gebyrer",
    description: "De fleste casinoer tager ikke gebyr for Trustly"
  },
  {
    icon: <CheckCircle className="w-6 h-6" />,
    title: "Ingen Registrering",
    description: "Brug direkte gennem din bankforbindelse"
  }
];

const trustlyCasinos = [
  {
    name: "Betsson",
    withdrawalTime: "0-15 minutter",
    rating: "5.0",
    bonus: "100% op til 500 kr"
  },
  {
    name: "LeoVegas", 
    withdrawalTime: "0-15 minutter",
    rating: "5.0",
    bonus: "Op til 1.000 kr"
  },
  {
    name: "Casumo",
    withdrawalTime: "0-24 timer",
    rating: "5.0", 
    bonus: "100% op til 1.000 kr"
  }
];

function TrustlyPage() {
  return (
    <>
      <Helmet>
        <title>Trustly Casino - Hurtige Udbetalinger på 0-15 Minutter | CasinoGuide.dk</title>
        <meta 
          name="description" 
          content="Lær alt om Trustly casinoer og få dine gevinster på 0-15 minutter. Guide til de bedste danske casinoer med Trustly betalingsmetode." 
        />
        <meta 
          name="keywords" 
          content="trustly casino, trustly udbetaling, hurtig casino udbetaling, instant banking casino"
        />
        <link rel="canonical" href="https://casinoguide.dk/trustly" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Trustly Casino - Hurtige Udbetalinger på 0-15 Minutter",
            "description": "Komplet guide til Trustly casinoer med øjeblikkelige udbetalinger",
            "author": {
              "@type": "Person", 
              "name": "Jonas Schütt Nielsen"
            },
            "publisher": {
              "@type": "Organization",
              "name": "CasinoGuide.dk"
            },
            "datePublished": "2025-11-25",
            "inLanguage": "da"
          })}
        </script>
      </Helmet>

      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          
          {/* Hero Section */}
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
                <span className="bg-gradient-to-r from-green-300 via-emerald-300 to-green-400 bg-clip-text text-transparent">
                  Trustly Casino
                </span>
                <br />
                <span className="text-blue-100">Hurtige Udbetalinger på 0-15 Minutter</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-blue-200 mb-8 leading-relaxed">
                Trustly er den hurtigste betalingsmetode på danske casinoer. Få dine gevinster direkte overført til din bankkonto på få minutter - uden registrering eller gebyrer.
              </p>
            </div>

            {/* Benefits Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {trustlyBenefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="bg-slate-800/50 rounded-xl p-6 border border-green-600/20 hover:border-green-500/40 transition-colors"
                >
                  <div className="flex items-center gap-4 mb-3">
                    <div className="text-green-400">
                      {benefit.icon}
                    </div>
                    <h3 className="text-xl font-bold text-green-300">
                      {benefit.title}
                    </h3>
                  </div>
                  <p className="text-blue-200">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>

            {/* How Trustly Works */}
            <div className="bg-slate-800/50 rounded-2xl p-8 md:p-12 border border-blue-700/30 mb-12">
              <h2 className="text-3xl font-bold mb-8 bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent">
                Sådan Fungerer Trustly Casino
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">1</span>
                  </div>
                  <h3 className="text-lg font-bold text-green-300 mb-2">Vælg Trustly</h3>
                  <p className="text-blue-200 text-sm">
                    Vælg Trustly som udbetalingsmetode på casinoet
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">2</span>
                  </div>
                  <h3 className="text-lg font-bold text-green-300 mb-2">Log ind i Bank</h3>
                  <p className="text-blue-200 text-sm">
                    Log sikkert ind i din bank gennem Trustly
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">3</span>
                  </div>
                  <h3 className="text-lg font-bold text-green-300 mb-2">Modtag Penge</h3>
                  <p className="text-blue-200 text-sm">
                    Pengene er på din konto inden for 0-15 minutter
                  </p>
                </div>
              </div>
            </div>

            {/* Best Trustly Casinos */}
            <div className="bg-slate-800/50 rounded-2xl p-8 md:p-12 border border-blue-700/30 mb-12">
              <h2 className="text-3xl font-bold mb-8 bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent">
                Bedste Trustly Casinoer 2025
              </h2>
              
              <div className="space-y-6">
                {trustlyCasinos.map((casino, index) => (
                  <div 
                    key={index}
                    className="bg-slate-900/50 rounded-xl p-6 border border-green-600/20 flex flex-col md:flex-row md:items-center justify-between gap-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-2">
                        <h3 className="text-xl font-bold text-green-300">{casino.name}</h3>
                        <div className="flex items-center gap-1">
                          <span className="text-yellow-400">⭐</span>
                          <span className="text-blue-200 font-medium">{casino.rating}</span>
                        </div>
                      </div>
                      <p className="text-blue-300 text-sm">{casino.bonus}</p>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-green-400 font-bold text-sm">⚡ {casino.withdrawalTime}</p>
                        <p className="text-blue-300 text-xs">Udbetalingstid</p>
                      </div>
                      <button className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold rounded-lg hover:from-green-500 hover:to-emerald-500 transition-all">
                        Besøg
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-slate-800/50 rounded-2xl p-8 md:p-12 border border-blue-700/30">
              <h2 className="text-3xl font-bold mb-8 bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent">
                Trustly FAQ
              </h2>
              
              <div className="space-y-6">
                <div className="bg-slate-900/50 rounded-xl p-6 border border-green-600/20">
                  <h3 className="text-xl font-bold text-green-300 mb-3">Er Trustly sikkert?</h3>
                  <p className="text-blue-200">
                    Ja, Trustly bruger samme sikkerhedsniveau som din netbank og er reguleret i Sverige.
                  </p>
                </div>
                
                <div className="bg-slate-900/50 rounded-xl p-6 border border-green-600/20">
                  <h3 className="text-xl font-bold text-green-300 mb-3">Koster Trustly penge?</h3>
                  <p className="text-blue-200">
                    De fleste casinoer tager ikke gebyr for Trustly-transaktioner, men tjek altid vilkårene.
                  </p>
                </div>
                
                <div className="bg-slate-900/50 rounded-xl p-6 border border-green-600/20">
                  <h3 className="text-xl font-bold text-green-300 mb-3">Hvilke banker understøtter Trustly?</h3>
                  <p className="text-blue-200">
                    Alle større danske banker inklusiv Danske Bank, Nordea, Jyske Bank og Sydbank.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

export default TrustlyPage;